<template>
    <div class="panel-box" :style="{ width }">
        <p class="box-title" v-dompurify-html="title" :style="{ color }"></p>
        <div class="change-value">
            <div class="change-value-text">
                {{ `${props.value > 0 ? '+' : ''}${formattedCount}` }}
            </div>
            <span class="change-value-unit body-3--regular">{{ unit }}</span>
        </div>
        <div class="change-percentage caption--regular" v-dompurify-html="subTitle"></div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

interface Props {
    value: number;
    unit: string;
    title: string;
    subTitle: string;
    color: string;
    width: string;
    shouldAnimate: boolean; // 부모에서 신호로 전달
}

const props = defineProps<Props>();

const currentCount = ref(0.0);
const MAX_DURATION = 2000;

const calculateDuration = (value: number) => {
    const baseDuration = Math.abs(value) * 500;
    return Math.min(baseDuration, MAX_DURATION);
};

watch(
    () => props.shouldAnimate,
    newVal => {
        if (newVal) {
            if (props.value === 0) {
                currentCount.value = 0; // 0이면 바로 0 세팅하고
                return; // 애니메이션 안 함
            }

            animateCount(0, props.value, calculateDuration(props.value));
        }
    },
);

// 숫자 포맷 함수 (3자리마다 , 삽입)
const numberWithCommas = (x: string) => {
    const parts = x.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
};

const formattedCount = computed(() => {
    return numberWithCommas(currentCount.value.toFixed(1));
});

const animateCount = (start: number, end: number, duration: number) => {
    const startTime = performance.now();

    const update = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        currentCount.value = start + (end - start) * progress;

        if (progress < 1) {
            requestAnimationFrame(update);
        } else {
            currentCount.value = end;
        }
    };

    requestAnimationFrame(update);
};
</script>

<style scoped lang="scss">
.panel-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 1rem;
    background-color: $grey-1;
    border-radius: 1rem;

    .box-title {
        font-weight: 400;
        font-size: 1.2rem;
        line-height: 150%;
        text-align: center;
    }

    .change-value {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        justify-content: center;

        .change-value-text {
            font-weight: 700;
            font-size: 2.8rem;
            line-height: 170%;
            text-align: center;
        }

        .change-value-unit {
            padding-bottom: 0.8rem;
            color: $grey-7;
        }
    }

    .change-percentage {
        color: $grey-7;
    }
}
</style>
